//Services
import { gen2Request } from 'src/services/gen_2_http_reqs';
import { getScheduleList } from 'src/helpers/get_schedules_gen_2';
//Types
import type { Device } from 'src/types/Device';
import type { SettingRequest } from 'src/types/requests/settings/setting';
import type { CuryProfile } from 'src/types/Cury';

/**
 * Deletes all existing schedules from a Cury device.
 *
 * This function processes a list of existing schedules and deletes each one individually.
 * It safely handles errors for individual schedule deletions and continues processing
 * even if some deletions fail.
 *
 * @param {any} device - The Cury device object containing device information and connection details
 * @param {any} listRes - The response object from getScheduleList containing the list of existing schedules
 *
 * @returns {Promise<void>} A promise that resolves when all schedule deletions are attempted
 *
 * @example
 * // Delete all existing schedules
 * const scheduleList = await getScheduleList(device);
 * await deleteExistingSchedules(device, scheduleList);
 *
 */
export const deleteExistingSchedules: (
	device: Device,
	listRes: SettingRequest,
) => Promise<void> = async (device: Device, listRes: SettingRequest) => {
	try {
		if (listRes?.isok && (listRes as any)?.data?.jobs) {
			const schedules = (listRes as any).data.jobs;
			for (const schedule of schedules) {
				if (schedule.id !== undefined) {
					try {
						await gen2Request(device, 'schedule.delete', { id: schedule.id });
					} catch (err) {
						console.error('Error deleting schedule:', err);
					}
				}
			}
		}
	} catch (err) {
		console.error('Error getting schedule list:', err);
	}
};

/**
 * Creates a new schedule on a Cury device.
 *
 * This function sends a request to create a new schedule with the specified parameters.
 * The schedule can include timing information, method calls, and enable/disable settings.
 *
 * @param {any} device - The Cury device object containing device information and connection details
 * @param {any} params - The schedule parameters object containing:
 *   - `timespec`: {string} Cron expression defining when the schedule should run
 *   - `calls`: {Array} Array of method calls to execute when the schedule triggers
 *   - `enable`: {boolean} Whether the schedule should be enabled
 *
 * @returns {Promise<any>} A promise that resolves to the response from the schedule creation request
 *
 * @example
 * // Create a simple schedule
 * const scheduleParams = {
 *   timespec: '0 30 8 * * 1-5',
 *   calls: [{ method: 'cury.set', params: { intensity: 50, slot: 'left' } }],
 *   enable: true
 * };
 * const result = await createSchedule(device, scheduleParams);
 *
 */
export const createSchedule: (device: Device, params: any) => Promise<SettingRequest> = async (
	device: Device,
	params: any,
) => {
	return gen2Request(device, 'schedule.create', params);
};

/**
 * Deletes all existing schedules for a specific Cury device.
 *
 * This is a convenience function that combines getting the schedule list and deleting
 * all existing schedules. It first retrieves all current schedules for the device,
 * then deletes each one individually.
 *
 * @param {any} device - The Cury device object containing device information and connection details
 *
 * @returns {Promise<void>} A promise that resolves when all schedule deletions are attempted
 *
 * @example
 * // Clear all schedules for a device
 * await deleteExistingSchedulesForDevice(curyDevice);
 * console.log('All schedules cleared');
 *
 */
export const deleteExistingSchedulesForDevice: (device: Device) => Promise<void> = async (
	device: Device,
) => {
	const listRes = await getScheduleList(device);
	await deleteExistingSchedules(device, listRes);
};

/**
 * Creates a complete schedule for a Cury profile with multiple time slots.
 *
 * This function processes a profile's schedule configuration and creates individual
 * schedules for each time slot. It handles both regular time slots and overnight
 * schedules that cross midnight. Each schedule is created with appropriate cron
 * expressions and method calls to control the Cury device.
 *
 * @param {any} device - The Cury device object containing device information and connection details
 * @param {any} profile - The profile object containing schedule configuration:
 *   - `schedule.hours`: {Array} Array of hour slot objects with time, slot, and intensity
 *   - `schedule.days`: {Array} Array of day abbreviations (Mon, Tue, etc.)
 *   - `auto_switching`: {boolean} Whether the profile should auto-switch
 *   - `mode_id`: {string} Unique identifier for the profile mode
 *
 * @returns {Promise<void>} A promise that resolves when all schedules are created
 *
 * @example
 * // Create schedules for a bedroom profile
 * const bedroomProfile = {
 *   mode_id: 'bedroom',
 *   auto_switching: true,
 *   schedule: {
 *     days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
 *     hours: [
 *       { time: '06:00-10:00', slot: 'left', intensity: 55 },
 *       { time: '10:00-20:00', slot: 'right', intensity: 40 }
 *     ]
 *   }
 * };
 * await createProfileSchedule(device, bedroomProfile);
 *
 */
export const createProfileSchedule: (device: Device, profile: CuryProfile) => Promise<void> = async (
	device: Device,
	profile: CuryProfile,
) => {
	for (const hourSlot of profile.schedule.hours) {
		const [startTime, endTime] = hourSlot.time.split('-');
		const [startHour, startMin] = startTime.split(':').map(Number);
		const [endHour, endMin] = endTime.split(':').map(Number);

		const dayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 } as const;
		const cronDays = profile.schedule.days.map((day: keyof typeof dayMap) => dayMap[day]).join(',');
		const timespec = `0 ${startMin} ${startHour} * * ${cronDays}`;

		const isOvernight = endHour < startHour || (endHour === startHour && endMin < startMin);

		if (isOvernight) {
			const startParams = {
				timespec,
				calls: [
					{
						method: 'cury.set',
						params: { intensity: hourSlot.intensity, slot: hourSlot.slot, id: 0 },
						name: `${profile.mode_id}_start`,
					},
				],
				enable: profile.auto_switching,
			};
			await createSchedule(device, startParams);

			const endTimespec = `0 ${endMin} ${endHour} * * ${cronDays}`;
			const endParams = {
				timespec: endTimespec,
				calls: [
					{
						method: 'cury.set',
						params: { intensity: 0, slot: hourSlot.slot, id: 0 },
						name: `${profile.mode_id}_end`,
					},
				],
				enable: profile.auto_switching,
			};
			await createSchedule(device, endParams);
		} else {
			const params = {
				timespec,
				calls: [
					{
						method: 'cury.set',
						params: { intensity: hourSlot.intensity, slot: hourSlot.slot, id: 0 },
						name: profile.mode_id,
					},
				],
				enable: profile.auto_switching,
			};
			await createSchedule(device, params);
		}
	}
};
